"use client";

import { useState, type CSSProperties } from "react";
import Button from "@dt/Button";
import styles from "./ClientLogoMarquee.module.css";
import sizes from "./ClientLogoMarquee.sizes.module.css";
import clientLogos from "./clientLogos.json";
import { CLIENT_LOGO_SPRITE_URL } from "./clientLogoSprite.generated";
import { useAnimationContext } from "../../lib/animation";
import { useTranslate } from "../../lib/translation";

type ClientLogo = {
  /** File under /public/logos/clients/. */
  file: string;
  alt: string;
  /** Manual optical correction, read by build:logo-sizes only. */
  optical?: number;
};

/* Order and names live in clientLogos.json; each logo's optical size is
   generated from it by npm run build:logo-sizes. */
const logos: ClientLogo[] = clientLogos;

/* Object literals so build:icons registers both Phosphor glyphs. */
const pauseControl = { icon: "pause" };
const resumeControl = { icon: "play" };

const marqueeTrackStyle = {
  "--marquee-width": "50%",
} as CSSProperties;

function ClientLogoSemanticList() {
  return (
    <ul className="sr-only">
      {logos.map((logo) => (
        <li key={logo.file}>{logo.alt}</li>
      ))}
    </ul>
  );
}

/* Each logo is a <use> of one shared sprite file: a single request, found
   while the HTML parses and cached immutably, instead of 21 lazy <img>
   requests that only started as each logo neared the viewport, so a dropped
   connection on a phone left slots blank. A fixed aspect ratio per logo
   (sizes CSS) gives the lane its final width before paint; late image loads
   used to widen it and make the percentage-based scroll jump. The symbol's
   own viewBox scales it into the box, so the outer svg needs none. */
function LogoItem({ logo }: { logo: ClientLogo }) {
  const name = logo.file.replace(/\.svg$/, "");
  return (
    <div className="flex shrink-0 items-center">
      <svg
        aria-hidden="true"
        focusable="false"
        className={`client-logo-marquee-image ${sizes.logo} ${sizes[name]}`}
      >
        <use href={`${CLIENT_LOGO_SPRITE_URL}#client-logo-${name}`} />
      </svg>
    </div>
  );
}

function LogoMarqueeTrack({ laneLogos }: { laneLogos: ClientLogo[] }) {
  return (
    <div className="overflow-hidden">
      <div
        className={`client-logo-marquee-track ${sizes.track} flex w-max items-center py-3`}
        style={marqueeTrackStyle}
      >
        {[0, 1].map((duplicateIndex) => (
          <div
            key={duplicateIndex}
            data-marquee-group
            className="flex shrink-0 items-center gap-5 pr-5 md:gap-10 md:pr-10"
            aria-hidden={duplicateIndex > 0 ? "true" : undefined}
          >
            {laneLogos.map((logo) => (
              <LogoItem key={`${logo.file}-${duplicateIndex}`} logo={logo} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/** Props for ClientLogoMarquee. */
export interface ClientLogoMarqueeProps {
  /** Accessible name for the marquee section. */
  ariaLabel: string;
  /** Label for the control that pauses the marquee. Defaults to the translated homeClientLogosPause label. */
  pauseLabel?: string;
  /** Label for the control that resumes the marquee. Defaults to the translated homeClientLogosResume label. */
  resumeLabel?: string;
  /** Additional CSS classes on the marquee section. */
  className?: string;
}

/**
 * ClientLogoMarquee component.
 */
export function ClientLogoMarquee({
  ariaLabel,
  pauseLabel: pauseLabelProp,
  resumeLabel: resumeLabelProp,
  className,
}: ClientLogoMarqueeProps) {
  const t = useTranslate();
  const pauseLabel = pauseLabelProp ?? t("homeClientLogosPause");
  const resumeLabel = resumeLabelProp ?? t("homeClientLogosResume");
  const { motionPreference, isReady } = useAnimationContext();
  const [isPaused, setIsPaused] = useState(false);

  // Render the animated marquee by DEFAULT. The animation is pure CSS
  // (no JS required to run) and reduced-motion is honoured by the CSS
  // @media query, so it is SSR-safe and keeps animating even if client
  // hydration is slow or never completes (e.g. mobile Safari over a dev
  // server). Only fall back to the static grid once we have positively
  // confirmed the visitor prefers reduced motion. Previously this gated on
  // !isReady, so any device where the AnimationProvider effect did not run
  // was stuck on the static grid.
  if (isReady && motionPreference === "reduced") {
    return (
      <section
        aria-label={ariaLabel}
        className={className ? `py-3 ${className}` : "py-3"}
      >
        <ClientLogoSemanticList />
        <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-5 md:gap-x-12 md:gap-y-8">
          {logos.map((logo) => (
            <LogoItem key={logo.file} logo={logo} />
          ))}
        </div>
      </section>
    );
  }

  return (
    <section
      aria-label={ariaLabel}
      className={
        className
          ? `client-logo-marquee-container relative overflow-hidden ${className}`
          : "client-logo-marquee-container relative overflow-hidden"
      }
      data-paused={isPaused ? "true" : undefined}
    >
      <ClientLogoSemanticList />
      <LogoMarqueeTrack laneLogos={logos} />
      <div className={styles.controls}>
        <Button
          variant="tertiary"
          size="sm"
          icon={(isPaused ? resumeControl : pauseControl).icon}
          // One pattern only: the label swaps to say what activating it does
          // next, so the control carries no aria-pressed (which would read
          // as "Resume ..., pressed").
          onClick={() => setIsPaused((paused) => !paused)}
        >
          {isPaused ? resumeLabel : pauseLabel}
        </Button>
      </div>
    </section>
  );
}
